import { createContext, useContext, type ComponentType, type LazyExoticComponent } from "react";
import type { ModuleManifest } from "@tc/sdk";
import { trackRequest } from "./motion.js";

export * from "./theme.js";
export * from "./components/index.js";

/** The browser half of a module: its pages, keyed by path relative to the module's base path. */
export interface WebModule {
  manifest: ModuleManifest;
  /** "" is the module's home page; "/new" → /<module id>/new. */
  pages: Record<string, LazyExoticComponent<ComponentType> | ComponentType>;
}

export function defineWebModule(m: WebModule): WebModule {
  return m;
}

/** What every page can know about the signed-in user and company (provided by the app shell). */
export interface ShellSession {
  user: { id: string; name: string; email: string; theme: string; isPlatformAdmin: boolean; avatar?: string | null };
  /** The person's own shortcuts (paths), or null for the suggested set. */
  shortcuts?: string[] | null;
  tenant: { name: string; slug: string; branding: import("./theme.js").Branding };
  /** Someone from head office or support is viewing the app as this person. */
  viewAs?: { name: string; reason: string } | null;
  roles: string[];
  sites: string[] | null;
  modules: string[];
  /** Modules still being connected: visible to company admins only. */
  integrating?: string[];
  /** How each of the user's sites is run today (commission / company / not set). */
  siteModels?: Record<string, string | null>;
  /** Modules added since set-up whose own short set-up is waiting ("Set up <module>"). */
  setupPending?: string[];
  /** The person's sites that sell fuel; null = not known. */
  fuelSites?: string[] | null;
  /** Moving from the old Tankerford Cloud: on only for the company still moving; hide everything about it when not. */
  oldCloud?: boolean;
  permissions: string[];
  /** The Terms of service: a company admin must accept each new version once for the company. */
  terms?: { version: string; accepted: { by: string; at: string } | null; mustAccept: boolean };
  /** Sign-in security for this person and session. */
  security?: { totp: boolean; passkeys: number; mustSetup2fa: boolean; trusted: boolean; how: string; idleLockMinutes: number; allowPin: boolean; allowPasskeys: boolean; methods?: ("totp" | "sms" | "email")[] };
}

export { ShellContext, type ShellSection, type ShellLink } from "./context.js";
import { ShellContext } from "./context.js";

/** The signed-in session, navigation and permission check, inside any page. */
export function useShell() {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error("useShell() must be used inside the app shell");
  return ctx;
}

/** An API error: the message to show, plus the server's code (e.g. "step-up", "need-2fa") and status. */
export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string, readonly data?: Record<string, unknown>) { super(message); }
}

/**
 * "Confirm it's you": the app shell registers a handler that asks for Face ID, the PIN or password + code. When a
 * request comes back needing it, api() asks, then retries the request once — so every page gets it for free.
 */
let confirmHandler: ((reason: string) => Promise<boolean>) | null = null;
export function onConfirmNeeded(h: ((reason: string) => Promise<boolean>) | null) { confirmHandler = h; }

/** JSON fetch with the platform's error shape: throws ApiError(message) on failure. */
export function api<T = unknown>(path: string, init?: { method?: string; body?: unknown }, retried = false): Promise<T> {
  return trackRequest(request<T>(path, init, retried));
}
async function request<T>(path: string, init?: { method?: string; body?: unknown }, retried = false): Promise<T> {
  const res = await fetch(path, {
    method: init?.method ?? (init?.body === undefined ? "GET" : "POST"),
    headers: init?.body === undefined ? undefined : { "content-type": "application/json" },
    body: init?.body === undefined ? undefined : JSON.stringify(init.body),
    credentials: "same-origin",
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string; code?: string };
  if (!res.ok) {
    if (res.status === 403 && data.code === "step-up" && confirmHandler && !retried && !path.startsWith("/api/auth/")) {
      if (await confirmHandler(data.error ?? "")) return api<T>(path, init, true);
    }
    throw new ApiError(data.error ?? `Request failed (${res.status})`, res.status, data.code, data as Record<string, unknown>);
  }
  return data;
}

export interface UploadedFile { id: string; name: string; mime: string; size: number; site: string | null; url: string }

/** Upload one file for a module (and site). The server checks the real file type and your access. */
export async function uploadFile(module: string, site: string | null, file: File): Promise<UploadedFile> {
  const form = new FormData();
  form.append("module", module);
  if (site) form.append("site", site);
  form.append("file", file);
  const res = await fetch("/api/files", { method: "POST", body: form, credentials: "same-origin" });
  const data = (await res.json().catch(() => ({}))) as UploadedFile & { error?: string };
  if (!res.ok) throw new Error(data.error ?? "Upload failed");
  return data;
}

export { FilePicker, Thumbs, Tabs, EmptyState, Skeleton, Loading, PageSkeleton, CountUp, Roll } from "./components/extra.js";
export { Icon, iconNames, type IconName } from "./components/icons.js";
export { NozlDrop, NozlIcon, NozlLogo, PoweredByNozl } from "./components/brand.js";
export { Sheet, AllClear, lockScroll } from "./components/sheet.js";
export { RowSheet, RowEdit, CloseButton, useRowClose, rememberOpener } from "./components/rowsheet.js";
export { BringInHistory } from "./components/history.js";
export { SitePicker, SiteSwitch, SiteList, useSiteChoice, readSites, saveSites, sitesLabel, type PickSite, type SiteChoice } from "./components/sitepicker.js";
export { ProductImage } from "./components/productimage.js";
export { CompanyLogo, prepareLogo } from "./components/logo.js";
export { Avatar, avatarSrc, avatarSvg, AVATAR_IDS } from "./components/avatars.js";
export { QuickActions, ForecourtScene } from "./components/actions.js";
export { transition, whenSettled, afterTransitions, installGlow, fadeAfter } from "./motion.js";
export { Delta, Sparkline, StatCard, RingGauge, Donut, Columns, League, ActionTiles, Timeline, GradeBadge, gradeName, MetricStrip, greeting, fmt, type Segment, type LeagueRow } from "./components/charts.js";

/** UK dates everywhere in the UI: 09/10/26. Accepts "2026-10-09", a full ISO time or a Date. */
export function ukDate(v: string | Date | null | undefined): string {
  if (!v) return "—";
  const d = typeof v === "string" ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T12:00:00Z` : v) : v;
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit", timeZone: "Europe/London" });
}
/** 09/10/26 14:05, UK time. */
export function ukDateTime(v: string | Date | null | undefined): string {
  if (!v) return "—";
  const d = typeof v === "string" ? new Date(v) : v;
  return `${ukDate(d)} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" })}`;
}
/** "2026-04-01" to "2027-03-31" → "01/04/26 – 31/03/27". */
export const ukRange = (a: string, b: string) => `${ukDate(a)} – ${ukDate(b)}`;

/** How a site is run, in words. */
export const OPERATING_MODEL_SHORT: Record<string, string> = { commission: "Commission", operator: "Operator managed", company: "Company" };
export const OPERATING_MODEL_LABEL: Record<string, string> = { commission: "Commission operator", operator: "Operator managed (weekly fee)", company: "Company managed" };
export { InvoiceDocument, type InvoiceLine, type InvoiceParty } from "./components/invoice.js";
